/**
 * 1 - Destroy a Munitions Depot (03 §2): ammo shack (lootable), explosives magazine
 * (bunker-class) and fuel dump, well apart inside a walled perimeter with two gates;
 * 2-4 sentries and 1-2 MG nests. The loud tutorial mission: no quiet rule.
 */

import { siteEmplacement } from '../emplacements';
import type { Point } from '../types';
import { checker, clutter, guardAtDoor, guardsAbout, insideRect, required, structure, targetsOf } from './helpers';
import type { ArchetypeSpec } from './types';

/** Chained secondaries reach 10 m: the three must not set each other off (03 §2 asks for 14 m). */
const APART = 16;

export const munitionsDepot: ArchetypeSpec = {
  id: 'munitions-depot',
  name: 'DESTROY A MUNITIONS DEPOT',
  nativeBiome: 'desert',
  baseThreat: 2,
  lengthTiles: 150,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'wall',
  sealed: false,
  layout: 'compound',
  size: { w: [54, 62], d: [32, 34] },
  river: false,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    const placed: Point[] = [];
    const covered = new Set<string>();
    let nests = g.threat >= 3 ? 2 : 1;
    // The nests go in first, while there is room to give each gate one with a clear field of fire.
    for (let round = 0; round < 3 && nests > 0; round++) {
      for (const gate of g.ctx.plan.gates) {
        const inward = gate.side === 'south' ? 0 : gate.side === 'west' ? 6 : -6;
        const cover = { x: gate.x + inward, z: gate.side === 'south' ? gate.z : gate.z + 2 };
        if (nests > 0 && !covered.has(gate.side) && siteEmplacement(g.ctx, g.rng, 'mgNest', cover, g.elite)) {
          covered.add(gate.side);
          nests--;
        }
      }
    }
    if (covered.size === 0) g.map.problems.push('munitions-depot: no room for an MG nest at a gate');

    const magazine = required(g, 'magazine', { target: true, minDist: APART, from: placed });
    const fuel = required(g, 'fuelTank', { target: true, minDist: APART, from: placed });
    const shack = required(g, 'ammoShack', { target: true, minDist: APART, from: placed });
    structure(g, 'hut', { minDist: 9, from: placed });
    structure(g, g.kit.shelter, { minDist: 8, from: placed });
    structure(g, 'alarmPost', { margin: 1 });
    clutter(g, 'barrel', 3, fuel, 5);
    clutter(g, 'crate', 4, shack, 6);

    for (const t of [shack, magazine, fuel].slice(0, 2 + g.rng.int(2))) guardAtDoor(g, t);
    guardsAbout(g, g.rng.int(2));
    return {
      objective: {
        kind: 'destroyTargets', label: 'DESTROY DEPOT', targetPropIds: [shack.id, magazine.id, fuel.id], minFraction: 1,
        rules: [{ type: 'recall', mode: 'normal' }],
      },
    };
  },

  asserts(def) {
    const { check, out } = checker('munitions-depot');
    const targets = targetsOf(def);
    for (const kind of ['ammoShack', 'magazine', 'fuelTank'] as const) {
      check(targets.filter((t) => t.kind === kind).length === 1, `needs exactly one ${kind} target`);
    }
    for (const a of targets) {
      for (const b of targets) {
        const d = Math.sqrt((a.x - b.x) * (a.x - b.x) + (a.z - b.z) * (a.z - b.z));
        if (a.id < b.id) check(d >= 14, `targets ${a.kind} and ${b.kind} are only ${d.toFixed(1)} m apart`);
      }
    }
    check(
      def.objective.kind === 'destroyTargets' && def.objective.minFraction === 1,
      'objective must be to destroy all three',
    );
    check(
      def.spawns.some((s) => s.mans !== undefined && insideRect(def.goalZone, s.x, s.z)),
      'needs an MG nest inside the perimeter',
    );
    return out;
  },
};
